// Shared UI building blocks. Anything that appears in more than one screen lives
// here as a single component invoked with props, never copy-pasted per screen
// (brief: architecture DRY). The invite-sharing block is the canonical
// example — it closes onboarding and fills the Partager screen, and is one
// component, `shareCard`, reused in both.

import { h } from './dom';
import { icon, type IconName } from './icons';
import { toast } from './toast';
import { qrImage } from './qr';
import { avatarFor } from '../core/avatar';
import { getSyncState, onSyncState, type SyncState } from '../core/syncState';
import { navigate, type Screen } from './router';

export type Navigate = (screen: Screen) => void;

// --- Primitives -------------------------------------------------------------

export function brand(size: 'sm' | 'lg' = 'sm'): HTMLElement {
  const px = size === 'lg' ? 40 : 30;
  return h(
    'div',
    { class: 'brand' },
    h('img', { class: 'logo', src: '/logo.png', alt: '', width: String(px), height: String(px) }),
    h('span', { class: 'brand-name', text: 'Cercle' }),
  );
}

export function avatarEl(name: string, seed: string, size = 48, opts: { ring?: boolean } = {}): HTMLElement {
  const a = avatarFor(name, seed);
  // Colours are applied via CSSOM (element.style), not a style attribute, to
  // stay within the strict style-src 'self' CSP — see ARCHITECTURE.md §11.
  const el = h('div', { class: 'avatar', text: a.initials });
  el.style.background = a.bg;
  el.style.color = a.fg;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.fontSize = `${Math.round(size / 2.5)}px`;
  // A gradient ring frames the avatar on the profile identity card.
  return opts.ring ? h('div', { class: 'avatar-ring' }, el) : el;
}

export function card(...children: (Node | string | null | false)[]): HTMLElement {
  return h('div', { class: 'card' }, ...children);
}

/** An icon in a soft round badge — the visual anchor of a "how it works" row. */
export function bullet(ic: IconName, title: string, body: string): HTMLElement {
  return h(
    'li',
    { class: 'onb-bullet' },
    h('span', { class: 'onb-bullet-badge' }, icon(ic, 20)),
    h(
      'span',
      { class: 'onb-bullet-text' },
      h('span', { class: 'onb-bullet-title', text: title }),
      h('span', { class: 'onb-bullet-body', text: body }),
    ),
  );
}

/** Label + value row, or nothing when the value is empty. */
export function field(label: string, value: string): HTMLElement | null {
  if (!value) return null;
  return h('div', { class: 'field' }, h('span', { class: 'field-label', text: label }), h('span', { class: 'field-value', text: value }));
}

/**
 * A page heading with an optional back affordance (≥44px tap target) and an
 * optional trailing element pinned to the right of the title line — used to keep
 * secondary chrome (the sync status, a settings entry) on the title's own row
 * instead of taking a separate line.
 */
export function pageHeader(title: string, back?: Screen, trailing?: Node | false): HTMLElement {
  const children: (Node | false)[] = [];
  if (back) {
    children.push(
      h('button', { class: 'back-btn', 'aria-label': 'Retour', onclick: () => navigate(back) }, icon('back', 20)),
    );
  }
  children.push(h('h1', { class: 'page-title', text: title }));
  if (trailing) children.push(h('div', { class: 'page-header-trailing' }, trailing));
  return h('header', { class: 'page-header' }, ...children);
}

/**
 * The heading for a top-level screen: a small uppercase eyebrow above a serif
 * title, with an optional right-aligned trailing element on the title row. Used
 * on Profil / Cercle / Partager to give each section a distinct, editorial feel.
 */
export function mainHeader(eyebrow: string, title: string, trailing?: Node | false): HTMLElement {
  const row = h('div', { class: 'page-header' }, h('h1', { class: 'page-title', text: title }));
  if (trailing) row.append(h('div', { class: 'page-header-trailing' }, trailing));
  return h('div', { class: 'main-header' }, h('p', { class: 'eyebrow', text: eyebrow }), row);
}

export function primaryButton(label: string, onclick: () => void, ic?: IconName): HTMLButtonElement {
  const kids: (Node | string)[] = ic ? [icon(ic, 18), label] : [label];
  return h('button', { class: 'btn btn-primary', onclick }, ...kids) as HTMLButtonElement;
}

export function ghostButton(label: string, onclick: () => void, ic?: IconName): HTMLButtonElement {
  const kids: (Node | string)[] = ic ? [icon(ic, 18), label] : [label];
  return h('button', { class: 'btn btn-ghost', onclick }, ...kids) as HTMLButtonElement;
}

/** Copy-to-clipboard button with an inline confirmation swap. */
export function copyButton(label: string, value: () => string, okMsg = 'ID Cercle copié'): HTMLButtonElement {
  const btn = h('button', {
    class: 'btn btn-ghost',
    onclick: async () => {
      try {
        await navigator.clipboard.writeText(value());
        toast(okMsg);
      } catch {
        toast('Copie impossible sur ce navigateur');
      }
    },
  }) as HTMLButtonElement;
  btn.append(icon('copy', 18), document.createTextNode(label));
  return btn;
}

// --- Invite links -----------------------------------------------------------

/**
 * A full, in-scope https invite link for a Circle ID. Because it lives under the
 * PWA's manifest scope (`/`), opening it on a device where Cercle is installed
 * can hand off to the installed app; everywhere else it opens the site in the
 * browser, where the visitor is guided to create an identity and add the sender
 * (see the `invite` screen). The Circle ID rides in the hash, so it stays a
 * client-only, deep-linkable value the server never sees.
 */
export function inviteUrl(circleId: string): string {
  return `${location.origin}/#/inviter/${encodeURIComponent(circleId)}`;
}

/**
 * Extract a Circle ID from either a raw ID or a pasted/scanned invite link, so
 * both work wherever we accept a Circle ID (paste field, QR scan).
 */
export function parseInviteId(text: string): string {
  const trimmed = text.trim();
  const match = trimmed.match(/#\/inviter\/([^?&#\s]+)/);
  return match ? decodeURIComponent(match[1]!) : trimmed;
}

// --- DRY: the invite-sharing block ------------------------------------------

/**
 * The "share my Circle ID" block on the Partager screen. The QR encodes the full
 * invite link (not the raw ID): a scan from a phone's camera opens the app/site
 * ready to add you, and the in-app scanner still works because it parses the
 * Circle ID back out of the link (see parseInviteId). The share/copy actions use
 * the same link; the raw Circle ID stays available to copy for manual entry.
 */
export function shareCard(circleId: string): HTMLElement {
  const link = inviteUrl(circleId);

  const copyToClipboard = async (value: string, ok: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast(ok);
    } catch {
      toast('Copie impossible sur ce navigateur');
    }
  };

  // The full Circle ID: selectable and tap-to-copy (affordance carried by
  // title/aria-label so the precious "object to hand over" stays uncluttered).
  const idStrip = h(
    'button',
    {
      class: 'id-strip',
      type: 'button',
      title: 'Toucher pour copier',
      'aria-label': 'Copier mon ID Cercle',
      onclick: () => void copyToClipboard(circleId, 'ID Cercle copié'),
    },
    circleId,
  );

  const actions: HTMLElement[] = [
    h(
      'button',
      { class: 'share-btn', type: 'button', onclick: () => void copyToClipboard(link, 'Lien d’invitation copié') },
      icon('copy', 16),
      'Copier le lien',
    ),
  ];
  if (typeof navigator.share === 'function') {
    actions.push(
      h(
        'button',
        {
          class: 'share-btn',
          type: 'button',
          onclick: async () => {
            try {
              await navigator.share({
                title: 'Ajoutez-moi sur Cercle',
                text: 'Ajoutez-moi sur Cercle, puis envoyez-moi votre ID Cercle en retour : on recevra chacun les coordonnées de l’autre, toujours à jour.',
                url: link,
              });
            } catch {
              /* user cancelled the share sheet */
            }
          },
        },
        icon('share', 16),
        'Partager…',
      ),
    );
  }

  return h(
    'div',
    { class: 'share-card' },
    h('p', { class: 'share-lead', text: 'Faites scanner ce code par votre proche, ou envoyez-lui le lien' }),
    h('div', { class: 'qr-wrap' }, qrImage(link, 6, 3)),
    idStrip,
    h('div', { class: 'share-actions' }, ...actions),
    h(
      'p',
      { class: 'share-reciprocity' },
      icon('lock', 14),
      h(
        'span',
        {
          text:
            'Ce n’est que la moitié du chemin : demandez-lui aussi son ID Cercle et ajoutez-le de votre côté, sinon vous ne recevrez pas ses coordonnées.',
        },
      ),
    ),
  );
}

// --- Sync indicator ---------------------------------------------------------

/** Compact French "time since" for the last sync, e.g. "il y a 2 min". */
function timeAgo(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 15) return 'à l’instant';
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const hrs = Math.floor(m / 60);
  if (hrs < 24) return `il y a ${hrs} h`;
  return `il y a ${Math.floor(hrs / 24)} j`;
}

/**
 * A small live status reflecting the background sync (brief §2). While a fetch
 * is in flight it shows a spinner + "Synchronisation…"; otherwise it shows how
 * long ago the last sync succeeded ("il y a 2 min"), refreshed on a timer so it
 * stays current between syncs. Hidden entirely when no relay is configured
 * (nothing to sync). Meant to sit inline on a page-header, right-aligned, to
 * save vertical space. Self-cleaning: it drops its subscription and timer once
 * it has been mounted and then removed from the DOM, so re-renders don't leak.
 */
export function syncIndicator(): HTMLElement {
  const spinner = h('span', { class: 'sync-spinner', 'aria-hidden': 'true' });
  const label = h('span', { class: 'sync-label' });
  const el = h('div', { class: 'sync-indicator small muted', role: 'status', 'aria-live': 'polite' }, spinner, label);

  const render = (s: SyncState): void => {
    el.hidden = s.status === 'disabled';
    el.classList.toggle('is-syncing', s.status === 'syncing');
    el.classList.toggle('is-warn', s.status === 'error' || s.status === 'offline' || s.pendingPublish);
    if (s.status === 'syncing') label.textContent = 'Synchronisation…';
    else if (s.pendingPublish && s.status === 'offline') label.textContent = 'Hors ligne · fiche en attente';
    else if (s.pendingPublish) label.textContent = 'Fiche en attente d’envoi';
    else if (s.status === 'offline') label.textContent = 'Hors ligne';
    else if (s.status === 'error') label.textContent = 'Synchronisation impossible';
    else label.textContent = s.lastSyncAt ? timeAgo(s.lastSyncAt) : '';
  };

  let seenConnected = false;
  const unsub = onSyncState((s) => {
    if (el.isConnected) seenConnected = true;
    else if (seenConnected) {
      unsub();
      return;
    }
    render(s);
  });
  // Keep the relative time fresh while idle; also serves as the timer's
  // self-clean once the element has been mounted and later detached.
  const tick = setInterval(() => {
    if (el.isConnected) {
      seenConnected = true;
      render(getSyncState());
    } else if (seenConnected) {
      clearInterval(tick);
      unsub();
    }
  }, 30_000);
  render(getSyncState());
  return el;
}

// --- Loading skeletons ------------------------------------------------------

/** A card of shimmer lines that reserves space so async content doesn't shift. */
export function skeletonCard(lines = 3): HTMLElement {
  const rows = Array.from({ length: lines }, (_, i) =>
    h('div', { class: 'skel-line' + (i === lines - 1 ? ' skel-line-short' : '') }),
  );
  return h('div', { class: 'card skeleton', 'aria-hidden': 'true' }, ...rows);
}

// --- App shell: retractable sidebar (desktop) ↔ bottom tab bar (mobile) ------
// The signature element. On desktop a retractable sidebar shows the section
// tree; below 768px the whole layout reorganises into a fixed bottom tab bar.

export type NavKey = 'dashboard' | 'circle' | 'shareId';

const NAV: { key: NavKey; label: string; ic: IconName; screen: Screen }[] = [
  { key: 'dashboard', label: 'Profil', ic: 'user', screen: { name: 'dashboard' } },
  { key: 'circle', label: 'Cercle', ic: 'users', screen: { name: 'circle' } },
  { key: 'shareId', label: 'Partager', ic: 'share', screen: { name: 'shareId' } },
];

const COLLAPSE_KEY = 'circle:sidebar-collapsed';

function sidebar(active: NavKey): HTMLElement {
  const links = NAV.map((item) => {
    const link = h(
      'button',
      { class: 'side-link' + (item.key === active ? ' is-active' : ''), onclick: () => navigate(item.screen) },
      icon(item.ic, 20, item.key === active),
      h('span', { class: 'side-label', text: item.label }),
    );
    if (item.key === active) link.setAttribute('aria-current', 'page');
    return link;
  });

  const toggle = h(
    'button',
    { class: 'side-toggle', 'aria-label': 'Replier ou déplier le menu' },
    icon('panel', 20),
    h('span', { class: 'side-label', text: 'Replier' }),
  );

  const aside = h(
    'aside',
    { class: 'sidebar' },
    h('div', { class: 'side-brand' }, brand()),
    h('nav', { class: 'side-nav' }, ...links),
    h('div', { class: 'spacer' }),
    toggle,
  );

  toggle.addEventListener('click', () => {
    const shell = aside.closest('.app-shell');
    const collapsed = shell?.classList.toggle('is-collapsed');
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  });

  return aside;
}

function tabbar(active: NavKey): HTMLElement {
  return h(
    'nav',
    { class: 'tabbar', 'aria-label': 'Navigation principale' },
    ...NAV.map((item) => {
      const tab = h(
        'button',
        { class: 'tab' + (item.key === active ? ' is-active' : ''), onclick: () => navigate(item.screen) },
        icon(item.ic, 22, item.key === active),
        h('span', { class: 'tab-label', text: item.label }),
      );
      if (item.key === active) tab.setAttribute('aria-current', 'page');
      return tab;
    }),
  );
}

/** Wrap screen content in the persistent navigation chrome. */
export function appShell(active: NavKey, ...content: (Node | false)[]): HTMLElement {
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* ignore */
  }
  return h(
    'div',
    { class: 'app-shell' + (collapsed ? ' is-collapsed' : '') },
    sidebar(active),
    h('main', { class: 'content' }, h('div', { class: 'content-inner' }, ...content)),
    tabbar(active),
  );
}

/** Centered standalone layout for identity screens (no nav chrome). */
export function centered(...content: (Node | false)[]): HTMLElement {
  return h('div', { class: 'screen-centered' }, h('div', { class: 'centered-inner' }, ...content));
}
